import { isNotNull } from "drizzle-orm"
import {
  pgSchema,
  boolean,
  uniqueIndex,
  timestamp,
  jsonb,
  text,
  varchar,
  integer,
  index,
  AnyPgColumn,
} from "drizzle-orm/pg-core"
import { id_length, ulidId } from "./shared"

export const importCertificateSchema = pgSchema("ic")

/**
 * Import certificate table.
 */
export const importCertificate = importCertificateSchema.table(
  "import_certificate",
  {
    id: ulidId(),
    certificate_number: text("certificate_number").notNull(),
    export_country: text("export_country").notNull(), // Two digit iso code
    type_code: integer(
      "type_code",
      /*enum: [
        657, // Re-export Phyto
        851, // Phytosanitary
        852, // Sanitary
      ], */
    ).notNull(),
    status_code: integer("status_code").notNull(),
    status: text("status", {
      enum: [
        "UNRECOGNISED",
        "REPLACEMENT_REQUESTED",
        "APPROVED",
        "WITHDRAWN",
        "REJECTED",
        "REPLACED",
        "ISSUED",
      ],
    }).notNull(),
    issue_date: timestamp("issue_date"),
    certificate: jsonb("certificate"), // entire certificate XML parsed to json
    xml_checksum: varchar("xml_checksum", {
      length: 64,
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    updatedBy: text("updated_by"),
    replacing_certificate_number: text("replacing_certificate_number"),
    replaced_by: varchar("replaced_by", { length: id_length }).references(
      (): AnyPgColumn => importCertificate.id,
    ),
    replacing: varchar("replacing", { length: id_length }).references(
      (): AnyPgColumn => importCertificate.id,
    ),
    replacement_reason: text("replacement_reason"),
    pdf_generated: text("pdf_generated", {
      enum: ["NOT_GENERATED", "IN_PROGRESS", "GENERATED"],
    })
      .notNull()
      .default("NOT_GENERATED"),
    execution_id: text("execution_id"), // record state machine execution id which inserted this certificate
  },
  (table) => {
    return {
      certificateIdx: uniqueIndex("ic_certificate_id_country").on(
        table.certificate_number,
        table.export_country,
      ),
      replacingCertificateIdx: index("idx_replacing_certificate_number")
        .on(table.export_country, table.replacing_certificate_number)
        .where(isNotNull(table.replacing_certificate_number)),
    }
  },
)

/* Embedded Referenced Documents */
export const importCertificateDocument = importCertificateSchema.table(
  "referenced_document",
  {
    id: ulidId(),
    importCertificate: varchar("import_certificate_id", { length: id_length })
      .notNull()
      .references(() => importCertificate.id, { onDelete: "cascade" }),
    uploadInProgress: boolean("upload_in_progress").notNull().default(true),
    filename: text("filename"),
    file_size_bytes: integer("file_size_bytes"),
    file_type: text("file_type"), // mime type
    description: text("description"), // UNCEFACT "Information"
    sha256_checksum: varchar("sha256_checksum", {
      length: 64,
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => {
    return {
      certificateIdx: index("ic_certificate_id_document").on(
        table.importCertificate,
      ),
    }
  },
)

/* Certificate Change History, e.g. initiated by replacement certificates */
export const importCertificateChangeHistory = importCertificateSchema.table(
  "change_history",
  {
    id: ulidId(),
    importCertificate: varchar("import_certificate_id", { length: id_length })
      .notNull()
      .references(() => importCertificate.id, { onDelete: "cascade" }),
    date: timestamp("date").notNull(),
    status: text("status").notNull(),
    related_certificate_number: text("related_certificate_number"), // Display value for source country cert number
    relatedCertificate: varchar("related_certificate_id", {
      length: id_length,
    }).references(() => importCertificate.id, { onDelete: "set null" }),
    note: text("note"),
    createdBy: varchar("created_by").notNull().default("System"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => {
    return {
      certificateIdx: index("ic_certificate_id_change_hist").on(
        table.importCertificate,
      ),
    }
  },
)

/* IPPC Hub Delivery Log */
export const importCertificateIPPCEnvelope = importCertificateSchema.table(
  "ippc_envelope",
  {
    id: ulidId(),
    certificate_id: varchar("certificate_id", { length: id_length })
      .notNull()
      .references((): AnyPgColumn => importCertificate.id, {
        onDelete: "cascade",
      }),
    hub_delivery_number: text("hub_delivery_number"),
    hub_tracking_info: text("hub_tracking_info"),
    hub_delivery_message: text("hub_delivery_message"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
)
